import { EventRsvp } from '@components/form/rsvp/rsvp';
import { ClientOnly } from '@components/general/force-client';
import { A } from '@components/general/link';
import { environment } from 'environment';
import Cookies from 'js-cookie';
import { RsvpNet } from 'networking';
import type { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import Image from 'next/image';
import nookies from 'nookies';
import { DonateForm } from 'pages/donate';
import { useState } from 'react';
import { initialRsvpEvents } from 'types';
import sweetLew from '../../assets/events/sweet-lew.png';
import hero from '../../assets/events/tailgate-hero.jpeg';
import homeTeam from '../../assets/general/hometeam.png';
import { WhiteArch } from '../../components/img/svgs';
import { Meta } from '../../components/meta/meta';

const cookieOptions = {
  maxAge: 30 * 24 * 60 * 60,
  path: '/',
  httpOnly: true,
  sameSite: 'lax',
  secure: environment.name !== 'development',
} as const;

export async function getServerSideProps({ req, res }: GetServerSidePropsContext) {
  const eventId = initialRsvpEvents.homeTeamTailgate2024.id;
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=86400');

  const currentRsvpEventIds = JSON.parse(nookies.get({ req }).rsvpEventIds || '[]').filter(
    (id: string) => !!id && id !== eventId,
  );
  nookies.set(
    { res },
    `${environment.cookies.prefix}rsvp-event-ids`,
    JSON.stringify([...currentRsvpEventIds, eventId]),
    cookieOptions,
  );
  const hasRegisteredCookie = !!nookies.get({ req })[`${environment.cookies.prefix}has-rsvpd-${eventId}`];
  console.log('hasRegisteredCookie', hasRegisteredCookie);

  const eventRes = await RsvpNet.public.get(eventId);
  if (!eventRes?.success) return { notFound: true };

  return { props: { event: eventRes.data, hasRegisteredCookie } };
}

export default function HomeTeamTailgate2024({
  event,
  hasRegisteredCookie: hasRegisteredServerCookie,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [justRegistered, setJustRegistered] = useState(false);

  const canRegister = new Date(event.rsvpStart) < new Date() && new Date(event.rsvpEnd) > new Date();

  const hasRegisteredCookie =
    hasRegisteredServerCookie || Cookies.get(`${environment.cookies.prefix}has-rsvpd-${event.id}`);
  const hasRegistered = justRegistered || !!hasRegisteredCookie;

  return (
    <main>
      <Meta
        title="Home Team Tailgate 2024"
        description="Join us for an evening at the warehouse to raise funds for Beds for Kids and spend time with our staff! Swing by for a tour, eat barbecue, and play some games."
      />
      <section className="relative h-[65vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Home Team"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>

      <section className="relative mx-auto max-w-3xl bg-white px-4 pb-12 text-blue-500">
        <h1 className="mx-auto mt-4 max-w-3xl text-center font-operetta text-4xl md:text-6xl">home team tailgate</h1>
        <h2 className="mt-4 text-center text-xl font-medium leading-6 md:text-2xl">
          Welcome to the inaugural <span className="whitespace-nowrap">Beds for Kids</span> Home Team Tailgate!
        </h2>
        <div className="flex gap-4 px-4">
          <Image
            src={homeTeam}
            alt="Beds for Kids Home Team Logo"
            className="-left-64 mx-auto mt-4 w-full max-w-32 md:mt-8 md:max-w-52 xl:absolute"
          />
          <Image
            src={sweetLew}
            alt="Sweet Lew's BBQ"
            className="-right-64 mx-auto mt-4 w-full max-w-32 md:mt-8 md:max-w-52 xl:absolute"
          />
        </div>
        <ol className="mx-auto my-4 grid w-full justify-center gap-y-2 text-center text-lg md:my-8 md:grid-cols-2 md:text-xl">
          <li>Friday, October 25th, 2024 from 5-8PM</li>
          <li>
            <A external href="https://goo.gl/maps/WwQZC76KE5WDCywK6" className="leading-6 hover:underline">
              4200 Revolution Park Drive
              <br />
              Charlotte, NC 28217
            </A>
          </li>
          <li>BBQ Dinner Provided</li>
          <li>Casual Attire</li>
        </ol>
        <p className="text-center text-lg leading-6">
          Join us for an evening at the warehouse to raise funds for Beds for Kids and spend time with our staff! Swing
          by for a tour, eat barbecue, and play some games. We'll have a brief programming moment around 6:30 to share
          more about our mission and how you can help us meet the need in our community.
        </p>
      </section>

      {canRegister &&
        (!hasRegistered ? (
          <section className="bg-white pb-12 text-blue-500">
            <EventRsvp event={event} title="RSVP for the" onRsvp={() => setJustRegistered(true)} />
          </section>
        ) : (
          <section className="bg-white px-4 pb-36 pt-24 text-blue-500">
            <div className="text-center">
              <h3 className="mb-2 text-2xl font-medium leading-6 md:text-3xl">Thank you for RSVPing!</h3>
              <p className="text-lg leading-6">We look forward to seeing you at the event!</p>
            </div>
          </section>
        ))}

      <section className="bg-white pb-24 text-blue-500">
        <h3 className="mb-8 px-4 text-center text-2xl font-medium leading-6 md:text-3xl">
          Can't make it or want to donate now?
        </h3>

        <ClientOnly>
          <DonateForm fixedCampaign={event.donationCampaign} noRecurring />
        </ClientOnly>
      </section>
    </main>
  );
}
